import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";

/** Dras kortet mer enn en tredjedel av bredden til venstre, avvises det. */
export const SWIPE_DISMISS_FRACTION = 1 / 3;
/** Lengden på utsveipet; DB-kallet gjøres først når kortet er ute av syne. */
export const SWIPE_LEAVE_MS = 420;

/** 0 i ro, 1 ved terskelen. Bare venstre teller: å dra til høyre gjør ingenting. */
export function swipeProgress(dx: number, width: number): number {
  if (width <= 0 || dx >= 0) return 0;
  return Math.min(1, -dx / (width * SWIPE_DISMISS_FRACTION));
}

export function shouldDismiss(dx: number, width: number): boolean {
  return width > 0 && -dx > width * SWIPE_DISMISS_FRACTION;
}

/**
 * Tinder-sveip for signalkortet (VIB-2958): dra til venstre eller kall
 * `dismiss()` (knapp, pil venstre). Kortet sveipes ut med «AVVIS»-stempel, og
 * `onDismiss` kalles når animasjonen er ferdig. Klikk på knapper og lenker i
 * kortet starter aldri et drag.
 */
export function useSwipeDismiss({ onDismiss, disabled = false }: { onDismiss: () => void; disabled?: boolean }) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const start = useRef<{ x: number; width: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const dismiss = useCallback(() => {
    if (leaving || disabled) return;
    setLeaving(true);
    timer.current = setTimeout(() => onDismissRef.current(), SWIPE_LEAVE_MS);
  }, [leaving, disabled]);

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (disabled || leaving || e.button !== 0) return;
    if ((e.target as HTMLElement).closest("a, button, input, textarea, select, [role='combobox'], [role='menuitem']")) return;
    // Ingen tekstmarkering eller innebygd bilde-/tekstdrag: det avbryter pekeren midt i sveipet.
    e.preventDefault();
    start.current = { x: e.clientX, width: e.currentTarget.offsetWidth };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (!start.current) return;
    setDx(Math.min(0, e.clientX - start.current.x));
  };
  const end = () => {
    if (!start.current) return;
    const { width } = start.current;
    start.current = null;
    setDragging(false);
    if (shouldDismiss(dx, width)) dismiss();
    else setDx(0);
  };

  const width = start.current?.width ?? 0;
  return {
    leaving,
    dragging,
    dx,
    /** 0–1: hvor tydelig «AVVIS» skal vises mens kortet dras. */
    progress: leaving ? 1 : swipeProgress(dx, width),
    dismiss,
    handlers: { onPointerDown, onPointerMove, onPointerUp: end, onPointerCancel: end },
  };
}
